/**
 * Bundle 102 (Phase 0) - useSurveyValidation.
 *
 * Extracted per-step validator. Reproduces the Bundle 86 behaviour +
 * the Bundle 100 (item A) tightening: alphanumeric >= 20 rule for
 * 30-char labels; recursive validation for direct_contact_list and
 * missed_connection_list; conditional-visibility check.
 *
 * Returns:
 *   validateStep(stepIndex) -> Array<{id, label, hint?}>
 *   stepErrors, setStepErrors  (state)
 *   evalConditional(q)  (visibility helper)
 */
import { useCallback, useState } from 'react';

const _alnumCount = (s) => {
  if (!s) return 0;
  let n = 0;
  const str = String(s);
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    if ((c >= 48 && c <= 57)
        || (c >= 65 && c <= 90)
        || (c >= 97 && c <= 122)) n += 1;
  }
  return n;
};

export const useSurveyValidation = ({ data, answers }) => {
  const [stepErrors, setStepErrors] = useState([]);

  const evalConditional = useCallback((q) => {
    if (!q.conditional) return true;
    const c = q.conditional;
    const v = (answers || {})[c.field];
    if ('equals' in c) return v === c.equals;
    if ('not_equals' in c) return v !== c.not_equals;
    if ('in' in c) {
      if (!Array.isArray(c.in)) return true;
      // Bundle 116 - `in` also matches when the parent value is
      // itself a LIST (chip): visible when ANY selected item
      // matches. Previously only matched scalars, which meant the
      // "Other" chip did not trigger its follow-up shorttext.
      if (Array.isArray(v)) return v.some((x) => c.in.includes(x));
      return c.in.includes(v);
    }
    return true;
  }, [answers]);

  const _validateListType = useCallback((q) => {
    const qid = q.id;
    const rows = Array.isArray((answers || {})[qid]) ? answers[qid] : [];
    const errs = [];
    const fields = q.capture_fields || [];
    if (q.type === 'direct_contact_list') {
      const count = Number((answers || {})[q.count_driver || 'direct_contact_count'] || 0);
      if (count <= 0 || rows.length === 0) return [{ id: qid, label: q.label }];
    }
    if (q.type === 'missed_connection_list') {
      const min = Number(q.min_requests || 1);
      const max = Number(q.max_requests || 2);
      if (rows.length < min || rows.length > max) {
        return [{ id: qid, label: q.label }];
      }
    }
    rows.forEach((row, idx) => {
      const i = idx + 1;
      fields.forEach((f) => {
        if (!f.required) return;
        const v = (row || {})[f.key];
        let ok = true;
        if (f.type === 'confirmation_checkbox') ok = v === true;
        else if (typeof v === 'string') ok = !!v.trim();
        else ok = v !== undefined && v !== null && v !== '';
        if (!ok) {
          errs.push({
            id: `${qid}[${i}].${f.key}`,
            label: `${q.label}: request ${i} - ${f.label}`,
          });
        }
      });
    });
    return errs;
  }, [answers]);

  const validateStep = useCallback((stepIndex) => {
    if (!data) return [];
    const out = [];
    for (const q of data.questions || []) {
      const qs = q.step || 1;
      if (qs !== stepIndex) continue;
      if (q.hidden) continue;
      if (!evalConditional(q)) continue;
      if (q.type === 'direct_contact_list' || q.type === 'missed_connection_list') {
        const sub = _validateListType(q);
        sub.forEach((e) => out.push(e));
        continue;
      }
      const v = (answers || {})[q.id];
      // Bundle 116 - optional-but-if-answered longtext with
      // `min_length_if_present`. Empty stays valid; any typed
      // content must clear the meaningful-chars floor.
      if (!q.required && q.type === 'longtext' && q.min_length_if_present) {
        if (v !== undefined && v !== null && String(v).trim()) {
          const min = Number(q.min_length_if_present);
          const trimmed = String(v).trim();
          const alnum = _alnumCount(trimmed);
          if (trimmed.length < min || (min >= 30 && alnum < 20)) {
            out.push({
              id: q.id,
              label: q.label,
              hint: `At least ${min} characters (and 20 letters/numbers) needed.`,
            });
          }
        }
        continue;
      }
      if (!q.required) continue;
      // Bundle 116 - chips require min_select entries when required.
      if (q.type === 'chips') {
        const min = Number(q.min_select || 1);
        const max = Number(q.max_select || 0);
        if (!Array.isArray(v) || v.length < min) {
          out.push({
            id: q.id, label: q.label,
            hint: min === 1 ? 'Pick at least one.'
              : `Pick at least ${min}.`,
          });
          continue;
        }
        if (max && v.length > max) {
          out.push({
            id: q.id, label: q.label,
            hint: `Pick no more than ${max}.`,
          });
          continue;
        }
        continue;
      }
      // Bundle 116 - integer bounds enforced when required.
      if (q.type === 'integer') {
        const n = v === '' || v === undefined || v === null
          ? null : Number(v);
        if (n === null || Number.isNaN(n) || !Number.isInteger(n)) {
          out.push({ id: q.id, label: q.label,
                      hint: 'Enter a whole number.' });
          continue;
        }
        if (q.min !== undefined && n < Number(q.min)) {
          out.push({ id: q.id, label: q.label,
                      hint: `Must be at least ${q.min}.` });
          continue;
        }
        if (q.max !== undefined && n > Number(q.max)) {
          out.push({ id: q.id, label: q.label,
                      hint: `Must be at most ${q.max}.` });
          continue;
        }
        continue;
      }
      const empty = v === undefined || v === null
        || (typeof v === 'string' && !v.trim())
        || (Array.isArray(v) && !v.length);
      if (empty) {
        out.push({ id: q.id, label: q.label });
        continue;
      }
      if (q.type === 'longtext' && q.min_length) {
        const min = Number(q.min_length);
        const trimmed = String(v).trim();
        const alnum = _alnumCount(trimmed);
        if (trimmed.length < min || (min >= 30 && alnum < 20)) {
          out.push({
            id: q.id,
            label: q.label,
            hint: `At least ${min} characters (and 20 letters/numbers) needed.`,
          });
        }
      }
    }
    return out;
  }, [data, answers, evalConditional, _validateListType]);

  return { validateStep, stepErrors, setStepErrors, evalConditional };
};

export default useSurveyValidation;
